import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Truck, MapPin, CheckCircle } from 'lucide-react';
import { DeliveryKPIs, ChartTheme } from './types';
import { KPICard, KPICardGrid, BadgeKPI } from './KPICard';
import { getBaseChartOptions } from './chartUtils';
import { useTranslation } from 'react-i18next';

interface DeliverySectionProps {
    data: DeliveryKPIs;
    theme: ChartTheme;
}

export const DeliverySection: React.FC<DeliverySectionProps> = ({ data, theme }) => {
    const { t } = useTranslation();
    const baseOptions = getBaseChartOptions(theme);

    // Pie Chart - Répartition des transporteurs
    const carrierChartOptions: Highcharts.Options = {
        ...baseOptions,
        chart: {
            ...baseOptions.chart,
            type: 'pie',
            height: 300,
        },
        title: {
            ...baseOptions.title,
            text: t('admin.dashboard.analytics.delivery.carrierDistribution'),
        },
        tooltip: {
            ...baseOptions.tooltip,
            pointFormat: `<b>{point.percentage:.1f}%</b> ({point.y} ${t('admin.dashboard.analytics.delivery.deliveries')})`,
        },
        plotOptions: {
            pie: {
                dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b>: {point.percentage:.1f}%',
                    style: { color: theme.textColor, fontSize: '11px' },
                },
                showInLegend: true,
            },
        },
        series: [{
            name: t('admin.dashboard.analytics.delivery.carriers'),
            type: 'pie',
            data: data.carrierDistribution.map((c, i) => ({
                name: c.carrier,
                y: c.count,
                color: theme.colors[i % theme.colors.length],
            })),
        }],
    };

    // Bar Chart - Coûts de livraison
    const costChartOptions: Highcharts.Options = {
        ...baseOptions,
        chart: {
            ...baseOptions.chart,
            type: 'column',
            height: 300,
        },
        title: {
            ...baseOptions.title,
            text: t('admin.dashboard.analytics.delivery.costDistribution'),
        },
        xAxis: {
            ...baseOptions.xAxis,
            categories: data.deliveryCostRanges.map(d => d.range),
        },
        yAxis: {
            ...baseOptions.yAxis,
            title: { text: t('admin.dashboard.analytics.delivery.ordersCount'), style: { color: theme.textColor } },
            allowDecimals: false,
        },
        tooltip: {
            ...baseOptions.tooltip,
            formatter: function () {
                return `<b>${this.x}</b><br/>${t('admin.dashboard.analytics.delivery.orders')}: ${this.y}`;
            },
        },
        plotOptions: {
            column: {
                borderRadius: 4,
                borderWidth: 0,
            },
        },
        legend: { enabled: false },
        series: [{
            name: t('admin.dashboard.analytics.delivery.orders'),
            type: 'column',
            data: data.deliveryCostRanges.map(d => d.count),
            color: theme.primaryColor,
        }],
    };

    const totalOrders = data.ordersWithTracking + data.ordersWithoutTracking;

    return (
        <section className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-info-10 rounded-lg">
                    <Truck className="h-5 w-5 text-info" />
                </div>
                <h2 className="text-xl font-bold text-foreground">{t('admin.dashboard.analytics.delivery.title')}</h2>
            </div>

            {/* KPI Cards */}
            <KPICardGrid columns={4}>
                <KPICard
                    title={t('admin.dashboard.analytics.delivery.kpis.averageCost')}
                    value={data.averageDeliveryCost}
                    format="currency"
                    icon={<Truck className="h-5 w-5 text-primary" />}
                    color="primary"
                    tooltip={t('admin.dashboard.analytics.delivery.kpis.averageCostTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.delivery.kpis.mainCarrier')}
                    value={data.mainCarrier}
                    icon={<Truck className="h-5 w-5 text-info" />}
                    color="info"
                    tooltip={t('admin.dashboard.analytics.delivery.kpis.mainCarrierTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.delivery.kpis.homeVsPickup')}
                    value={`${data.homeVsPickup.home} / ${data.homeVsPickup.pickup}`}
                    icon={<MapPin className="h-5 w-5 text-success" />}
                    color="success"
                    tooltip={t('admin.dashboard.analytics.delivery.kpis.homeVsPickupTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.delivery.kpis.withTracking')}
                    value={data.ordersWithTracking}
                    format="number"
                    icon={<CheckCircle className="h-5 w-5 text-success" />}
                    color="success"
                    tooltip={t('admin.dashboard.analytics.delivery.kpis.withTrackingTooltip')}
                />
            </KPICardGrid>

            {/* Tracking Badges */}
            <div className="flex flex-wrap gap-3">
                <BadgeKPI
                    label={t('admin.dashboard.analytics.delivery.kpis.withTracking')}
                    value={data.ordersWithTracking}
                    total={totalOrders}
                    variant="success"
                />
                <BadgeKPI
                    label={t('admin.dashboard.analytics.delivery.kpis.withoutTracking')}
                    value={data.ordersWithoutTracking}
                    total={totalOrders}
                    variant={data.ordersWithoutTracking > 0 ? 'warning' : 'success'}
                />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-surface border border-border rounded-xl p-4">
                    <HighchartsReact highcharts={Highcharts} options={carrierChartOptions} />
                </div>
                <div className="bg-surface border border-border rounded-xl p-4">
                    <HighchartsReact highcharts={Highcharts} options={costChartOptions} />
                </div>
            </div>
        </section>
    );
};
